/* eslint-disable @typescript-eslint/no-explicit-any */
import type { User } from '@supabase/supabase-js';
import type { Database } from '@pizzeria/types/database.types';

import { supabase } from '../lib/supabaseClient';
import { upsertDriver } from './drivers';

export type StaffRole = 'admin' | 'staff' | 'manager' | 'driver';

type RoleRow = Database['public']['Tables']['user_roles']['Row'];

export interface StaffAssignment {
  id: string;
  role: RoleRow['role'];
  restaurantId: string | null;
  restaurantSlug: string | null;
  restaurantName: string | null;
}

export interface StaffUserRecord {
  id: string;
  email: string | null;
  fullName: string | null;
  phone: string | null;
  role: StaffRole | 'unknown';
  assignments: StaffAssignment[];
  restaurantSlugs: string[];
  createdAt: string | null;
  lastSignInAt: string | null;
  raw: User | Record<string, unknown> | null;
}

const callAdminStaff = async <T>(action: string, payload: Record<string, unknown> = {}): Promise<T> => {
  const { data, error } = await supabase.functions.invoke('admin-staff', {
    body: { action, payload },
  });
  if (error) {
    throw error;
  }
  if (data?.error) {
    throw new Error(data.error);
  }
  return data?.data as T;
};

const ensureDriverRecord = async (userId: string, fullName?: string | null, phone?: string | null) => {
  // Best-effort: keep driver profile in sync when role=driver is set
  try {
    await upsertDriver({
      id: userId,
      fullName: fullName ?? undefined,
      phone: phone ?? undefined,
      isOnline: false,
    });
  } catch (_err) {
    // Non-blocking: server-side function also ensures driver record creation.
  }
};

export const findUserByEmail = async (_email: string): Promise<User | null> => {
  // Retained for compatibility; now handled via Edge Function in lookupStaffUserByEmail.
  return null;
};

export async function listStaffUsers(): Promise<StaffUserRecord[]> {
  const data = await callAdminStaff<StaffUserRecord[]>('list_staff');
  return data;
}

export interface CreateStaffUserInput {
  email: string;
  password?: string;
  fullName?: string;
  phone?: string;
  role?: StaffRole;
  inviteInsteadOfPassword?: boolean;
  restaurants?: string[];
}

export async function createStaffUser({
  email,
  password,
  fullName,
  phone,
  role = 'staff',
  inviteInsteadOfPassword = false,
  restaurants = [],
}: CreateStaffUserInput): Promise<StaffUserRecord> {
  const payload = {
    email,
    password,
    fullName,
    phone,
    role,
    inviteInsteadOfPassword,
    restaurants,
  };
  return await callAdminStaff<StaffUserRecord>('create_staff', payload);
}

export async function lookupStaffUserByEmail(email: string): Promise<StaffUserRecord | null> {
  const data = await callAdminStaff<StaffUserRecord | null>('lookup_by_email', { email });
  return data;
}

export async function updateStaffUserRole(userId: string, role: StaffRole) {
  const normalizedRole: StaffRole = role === 'manager' ? 'staff' : role;
  await callAdminStaff('update_staff_role', { userId, role: normalizedRole });

  if (normalizedRole === 'driver') {
    await ensureDriverRecord(userId);
  }
}

export async function deleteStaffUser(userId: string) {
  await callAdminStaff('delete_staff_user', { userId });
}
